import { useState, useCallback, useEffect, createContext, useContext, memo } from 'react'
import { createStore, useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import isEqual from 'react-fast-compare'
import { pick, reduce, withoutEmptyArrays } from 'utilities/object'

export const createZustandContext = name => {
    const context = createContext(createStore(() => ({ unprovided: true })))
    context.displayName = name
    return context
}

export const createUseClientState = ClientStateContext => (selector = (state => state), suppressWarning = false) => {
    const context = useContext(ClientStateContext)
    const unprovided = !!context?.getState?.()?.unprovided

    if(unprovided && !suppressWarning) {
        console.warn(`${ClientStateContext.displayName}ClientStateContext is missing!`)
    }

    const shallowSelector = useShallow(selector)
    return useStore(context, !!context && shallowSelector)
}

export const useEnabled = (queryOptions = {}) => {
    const [enabled, setEnabled] = useState(queryOptions?.enabled !== false)
    const enable = useCallback(() => setEnabled(true), [])

    useEffect(() => {
        setEnabled(queryOptions?.enabled !== false)
    }, [queryOptions?.enabled])

    return [enabled, enable]
}

export const createUseResolveInitialItems = useItems => (options = {}) => {
    const [items, setItems] = useState([])
    const [resolved, setResolved] = useState(false)

    const [enabled] = useEnabled(options?.queryOptions)

    const {
        items: initialItems,
        isFetched,
        ...rest
    } = useItems({
        queryOptions: {
            ...(options.queryOptions ?? null),
            enabled: enabled && !resolved
        },
        ...pick(options, 'path', 'filter', 'sorting', 'paging', 'params', 'adaptParams', 'adaptItems')
    })

    useEffect(() => {
        if(resolved || !isFetched) {
            return
        }

        setResolved(true)
        setItems(initialItems)
    }, [resolved, initialItems, isFetched])

    return {
        ...rest,
        items,
        setItems
    }
}

export const sanitizeFilter = filter => {
    const withoutFalsy = reduce(filter, (accumulator, value, key) => ({
        ...accumulator,
        ...((!!value || value === 0) ? { [key] : value } : null)
    }))

    return withoutEmptyArrays(withoutFalsy)
}

export const Replacer = memo(({ useClientState, props, on }) => {
    const {
        replace,
        ...stateIdentifiers
    } = useClientState(state => pick(state, 'replace', ...on))

    const propsIdentifiers = pick(props, ...on)

    useEffect(() => {
        if(!isEqual(stateIdentifiers, propsIdentifiers)) {
            replace(propsIdentifiers)
        }
    }, [JSON.stringify(stateIdentifiers), JSON.stringify(propsIdentifiers), replace])
}, (previous, next) => isEqual(
    pick(previous.props, ...next.on),
    pick(next.props, ...next.on)
))